// Cluster-hull drawing — FXC-8538 (Warp).
//
// Draws a soft, rounded outline behind a set of node positions so a
// clique / community / component reads as a region. Pure canvas + a tiny
// convex-hull; no dependencies. Coordinates are world-space (the
// force-graph canvas is already transformed by the global scale).

type Pt = [number, number];

const PAD = 11; // world-space padding around member nodes.

/** Outline the convex region of `pts`, filled faintly and stroked. */
export function drawGroupHull(
  ctx: CanvasRenderingContext2D,
  pts: Pt[],
  color: string,
  globalScale: number,
): void {
  if (pts.length === 0) return;
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5 / globalScale;
  ctx.lineJoin = "round";

  if (pts.length < 3) {
    // Too few points for a polygon — union of soft discs reads as a blob.
    for (const [x, y] of pts) {
      ctx.beginPath();
      ctx.arc(x, y, PAD * 1.7, 0, Math.PI * 2);
      ctx.globalAlpha = 0.12;
      ctx.fill();
      ctx.globalAlpha = 0.45;
      ctx.stroke();
    }
    ctx.restore();
    return;
  }

  const hull = convexHull(pts);
  const cx = hull.reduce((s, p) => s + p[0], 0) / hull.length;
  const cy = hull.reduce((s, p) => s + p[1], 0) / hull.length;
  const expanded: Pt[] = hull.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const len = Math.hypot(dx, dy) || 1;
    return [x + (dx / len) * PAD, y + (dy / len) * PAD];
  });

  roundedPolygonPath(ctx, expanded);
  ctx.globalAlpha = 0.12;
  ctx.fill();
  ctx.globalAlpha = 0.5;
  ctx.stroke();
  ctx.restore();
}

/** Andrew's monotone-chain convex hull. Returns CCW vertices. */
export function convexHull(points: Pt[]): Pt[] {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length <= 2) return pts;
  const cross = (o: Pt, a: Pt, b: Pt) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const lower: Pt[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper: Pt[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0)
      upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/** Trace a smooth closed path through polygon vertices (quadratic via edge midpoints). */
function roundedPolygonPath(ctx: CanvasRenderingContext2D, p: Pt[]): void {
  const n = p.length;
  ctx.beginPath();
  const mid = (a: Pt, b: Pt): Pt => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const start = mid(p[n - 1]!, p[0]!);
  ctx.moveTo(start[0], start[1]);
  for (let i = 0; i < n; i++) {
    const cur = p[i]!;
    const nxt = p[(i + 1) % n]!;
    const m = mid(cur, nxt);
    ctx.quadraticCurveTo(cur[0], cur[1], m[0], m[1]);
  }
  ctx.closePath();
}
